import * as React from 'react'

import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubTrigger,
  ContextMenuPortal,
  ContextMenuSubContent,
} from '~/sotano-kit/primitives/context-menu'
import {
  Block,
  ListBlock,
  blockTypesRecord,
  isListBlock,
  DEFAULT_LIST_VIEW,
  listViewLayouts,
  ListView,
} from '@basehub/data/replicache/index'
import { BlockLabelMoreActionsPortal } from '../block-wrapper'
import { Tabs, TabsList, TabsTrigger } from '~/sotano-kit/primitives/tabs'
import { useSelectedView } from './views/use-selected-view'
import { ListBlockViewIcon, ListBlockViewSwitcher } from './views/view-switcher'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import {
  EnterFullScreenIcon,
  Component1Icon,
  PlusIcon,
  TrashIcon,
  Pencil1Icon,
  CopyIcon,
  ChevronRightIcon,
  SymbolIcon,
} from '@radix-ui/react-icons'
import { useTabsStore } from '../../tabs/store'
import { Button } from '~/sotano-kit/primitives/button'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { Position } from '@basehub/data/replicache/mutators/repo-space/block/helpers/fractional-indexing'
import { invariant } from '@epic-web/invariant'
import { getId } from '@basehub/utils/id/index'
import { ListBlockFilterPopover } from './views/table/filter'
import { ArrowUpDownIcon, ListBlockSortPopover } from './views/table/sort'
import { Input } from '~/sotano-kit/primitives/input'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { getViewDefaultTitle } from '@basehub/data/replicache/schema/block/list/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/sotano-kit/primitives/dropdown-menu'
import { ToggleState, useToggleState } from '~/hooks/use-toggle-state'
import { clsx } from 'clsx'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { useRepoStore } from '../../repo-store'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { FullBleedContainer } from '../../../../_components/full-bleed-container'
import { ROW_HEIGHT } from './views/table/theme'
import { useEditorMode } from '../../editor-mode-provider'
import {
  RepoUndoOps,
  useRepoUndoManager,
} from '~/replicache/frontend/undo-redo/manager'
import { CreateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import {
  CreateListViewParams,
  DeleteListViewParams,
  UpdateListViewParams,
} from '@basehub/data/replicache/mutators/repo-space/block/mutators/list'
import { FunnelIcon } from '~/components/icons/funnel'

type ListBlockActionsProps = {
  block: ListBlock
  parent: Block
  path: string
  isFullView?: boolean
  shortcuts?: {
    fullscreen?: boolean
    template?: boolean
    createView?: boolean
    filter?: boolean
    sort?: boolean
  }
}

export const ListBlockActions = ({
  isFullView,
  parent,
  ...rest
}: ListBlockActionsProps) => {
  const filterPopoverToggleState = useToggleState()
  const sortPopoverToggleState = useToggleState()
  const block = rest.block
  const [selectedView] = useSelectedView(block)

  const forceMoreActionsVisible =
    !isFullView &&
    (filterPopoverToggleState.isOn ||
      sortPopoverToggleState.isOn ||
      Boolean(selectedView?.filter.length || selectedView?.sort.length))

  return (
    <BlockLabelMoreActionsPortal
      blockId={block.id}
      visible={forceMoreActionsVisible}
      className={clsx(forceMoreActionsVisible && '-mr-1', 'bg-dark-base')}
    >
      {isFullView ? (
        <FullBleedContainer
          maxWidth={1969}
          style={{ height: ROW_HEIGHT }}
          bleedContainerClassName="bg-dark-base -mt-2"
        >
          <Actions
            displayViews
            parent={parent}
            filterPopoverToggleState={filterPopoverToggleState}
            sortPopoverToggleState={sortPopoverToggleState}
            {...rest}
          />
        </FullBleedContainer>
      ) : (
        <Actions
          parent={parent}
          filterPopoverToggleState={filterPopoverToggleState}
          sortPopoverToggleState={sortPopoverToggleState}
          {...rest}
        />
      )}
    </BlockLabelMoreActionsPortal>
  )
}

const Actions = ({
  block,
  parent,
  path,
  displayViews,
  filterPopoverToggleState,
  sortPopoverToggleState,
  shortcuts,
}: {
  block: ListBlock
  parent: Block
  path: string
  displayViews?: boolean
  filterPopoverToggleState: ToggleState
  sortPopoverToggleState: ToggleState
  shortcuts?: ListBlockActionsProps['shortcuts']
}) => {
  const editorMode = useEditorMode()
  const readOnly = editorMode === 'read'
  const [selectedView] = useSelectedView(block)
  const addTab = useTabsStore((s) => s.addTab)
  const replicache = useRepoReplicache().data?.replicache
  const setFocus = useRepoStore((s) => s.setFocus)
  const { activeTabKey: blockId } = useDashboardSlugs()
  const treeManager = useWorkingTreeStore((s) => s.treeRef)
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const sourceBlockId = idToSourceBlockId(block)
  const focusOpTable = React.useMemo(() => {
    return {
      op: 'focus',
      args: {
        focus: {
          type: 'block:wrapper',
          blockId: sourceBlockId,
        },
        preferredTabKey: activeTabKey,
      },
    } satisfies RepoUndoOps[number]
  }, [sourceBlockId, activeTabKey])

  const handleNewRow = React.useCallback(
    async (position: Position) => {
      invariant(replicache, 'replicache should be defined')
      invariant(
        block.typeOptions !== null,
        'block.typeOptions should be defined'
      )
      const newBlockId = getId()
      const params: CreateBlockParams = {
        data: {
          id: newBlockId,
          title: '',
          type: blockTypesRecord.instance,
          typeOptions: {
            targetBlockId: block.typeOptions.targetBlockId,
          },
        },
        isoNow: new Date().toISOString(),
        position,
        path,
        idSeed: getId(),
        parentInstanceChildTarget: block.instanceChildTarget,
      }
      const { newBlock, undo } = await replicache.mutate.createBlock(params)

      if (!newBlock) return

      if (undo) {
        undoManager.current?.push({
          redo: [focusOpTable, { op: 'createBlock', args: params }],
          undo: [
            focusOpTable,
            { op: 'blockBatchMutations', args: { mutations: undo } },
          ],
        })
      }

      const activeDocument = blockId
        ? treeManager.current?.getNode(blockId, {
            fallbackKey: 'sourceBlockId',
          })?.value
        : null

      setFocus({
        type: 'block:document-title',
        blockId: idToSourceBlockId(newBlock),
        temporaryBackLink: {
          title: block.title || 'Untitled',
          id: idToSourceBlockId(activeDocument ?? block),
          edgeId: activeDocument ? idToSourceBlockId(block) : undefined,
        },
      })

      addTab({
        key: newBlock.id,
        partiallyOpen: true,
      })
    },
    [
      replicache,
      block,
      path,
      blockId,
      treeManager,
      setFocus,
      addTab,
      undoManager,
      focusOpTable,
    ]
  )

  return (
    <div className="gap-x-3 w-full flex items-center justify-between">
      {displayViews && <ListViews block={block} path={path} />}

      <div className="shrink-0 text-dark-faint flex gap-x-1.5 relative">
        {/* Open filters */}
        {shortcuts?.filter && (
          <ListBlockFilterPopover
            block={block}
            path={path}
            parent={parent}
            toggleState={filterPopoverToggleState}
          >
            <IconButton
              onClick={filterPopoverToggleState.handleToggle}
              role={`filter-trigger-${block.id}`}
              className={clsx(
                'space-x-1 text-xs leading-none [&>*]:pointer-events-none',
                selectedView?.filter.length &&
                  'bg-dark-control-base/15 text-dark-control-base hover:!bg-dark-control-base/30 px-2'
              )}
            >
              <FunnelIcon
                width={18}
                height={18}
                className="pointer-events-none"
              />
              {Boolean(selectedView?.filter.length) && (
                <span className="text-xs font-medium pointer-events-none">
                  {selectedView?.filter.length}
                </span>
              )}
            </IconButton>
          </ListBlockFilterPopover>
        )}

        {/* Open sort */}
        {shortcuts?.sort && (
          <ListBlockSortPopover
            block={block}
            parent={parent}
            path={path}
            toggleState={sortPopoverToggleState}
          >
            <IconButton
              onClick={sortPopoverToggleState.handleToggle}
              role={`sort-trigger-${block.id}`}
              className={clsx(
                'space-x-1 text-xs leading-none [&>*]:pointer-events-none',
                selectedView?.sort.length &&
                  'bg-dark-control-base/15 text-dark-control-base hover:!bg-dark-control-base/30 px-2'
              )}
            >
              <ArrowUpDownIcon
                width={18}
                height={18}
                className="pointer-events-none"
              />
              {Boolean(selectedView?.sort.length) && (
                <span className="text-xs font-medium pointer-events-none">
                  {selectedView?.sort.length}
                </span>
              )}
            </IconButton>
          </ListBlockSortPopover>
        )}

        {/* Open full view */}
        {shortcuts?.fullscreen && (
          <SimpleTooltip
            content={`Open ${selectedView?.layout ?? 'table'} in a single tab`}
          >
            <IconButton
              onClick={() => {
                addTab({
                  key: block.id,
                  partiallyOpen: true,
                })
              }}
            >
              <EnterFullScreenIcon width={18} height={18} />
            </IconButton>
          </SimpleTooltip>
        )}

        {/* Open template */}
        {shortcuts?.template && (
          <SimpleTooltip
            content={`Open ${selectedView?.layout ?? 'table'}'s template`}
          >
            <IconButton
              className="hover:!bg-dark-control-base/30"
              onClick={() => {
                addTab({
                  key: block.typeOptions.targetBlockId,
                  partiallyOpen: true,
                })
              }}
            >
              <Component1Icon
                width={18}
                height={18}
                className="text-dark-control-base"
              />
            </IconButton>
          </SimpleTooltip>
        )}

        {!readOnly && (
          <Button
            onClick={() => {
              if (readOnly) return
              handleNewRow({ type: 'first' })
            }}
            disabled={readOnly}
            data-new-row
            data-block-id={idToSourceBlockId(block)}
          >
            New Row
          </Button>
        )}
      </div>
    </div>
  )
}

const ListViews = ({ block, path }: { block: ListBlock; path: string }) => {
  const [selectedView, updateSelectedView] = useSelectedView(block)
  const replicache = useRepoReplicache().data?.replicache
  const treeManager = useWorkingTreeStore((s) => s.tree)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const tabsRef = React.useRef<HTMLButtonElement[]>([])
  const [howManyViewsFit, setHowManyTabsFit] = React.useState(0)
  const editorMode = useEditorMode()
  const readOnly = editorMode === 'read'
  const [isRenamingViewId, setIsRenamingViewId] = React.useState<string | null>(
    null
  )
  const [renamingInputValue, setRenamingInputValue] = React.useState(
    selectedView?.title || ''
  )
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const sourceBlockId = idToSourceBlockId(block)
  const focusOpTable = React.useMemo(() => {
    return {
      op: 'focus',
      args: {
        focus: {
          type: 'block:wrapper',
          blockId: sourceBlockId,
        },
        preferredTabKey: activeTabKey,
      },
    } satisfies RepoUndoOps[number]
  }, [sourceBlockId, activeTabKey])

  const views = React.useMemo(() => {
    if (!block.value.views?.length) return [DEFAULT_LIST_VIEW]

    return block.value.views
  }, [block.value.views])

  const updateTabsFit = React.useCallback(() => {
    let fit = 0

    const tabs = containerRef.current
    if (!tabs) return

    // calc how many tabs will fit
    tabsRef.current.reduce((acc, tab) => {
      const width = acc + tab.clientWidth
      const PADDING = 90

      if (width + PADDING < tabs.clientWidth) fit++

      return width
    }, 0)

    const _views = views.slice(0, fit)
    const selectedViewIsInList = _views.some(
      (view) => view.id === selectedView?.id
    )
    if (selectedViewIsInList || !selectedView) {
      if (fit < 1) fit = 1
      setHowManyTabsFit(fit)
      return
    }

    _views.pop()
    const viewsWithSelectedView = [..._views, selectedView]
    const tabsElementToDisplay = tabsRef.current.filter((tab) => {
      const tabIndex = viewsWithSelectedView.findIndex((view) => {
        return view.id === tab.dataset.viewId
      })

      return tabIndex !== -1
    })

    let updatedFit = 0

    tabsElementToDisplay.reduce((acc, tab) => {
      const width = acc + tab.clientWidth
      const PADDING = 90

      if (width + PADDING < tabs.clientWidth) updatedFit++

      return width
    }, 0)

    if (updatedFit < 1) updatedFit = 1
    setHowManyTabsFit(updatedFit)
  }, [selectedView, views])

  React.useEffect(() => {
    // resize observer over tabs
    const tabs = containerRef.current
    if (!tabs) return

    const observer = new ResizeObserver(updateTabsFit)

    observer.observe(tabs, { box: 'border-box' })

    return () => {
      observer.disconnect()
    }
  }, [howManyViewsFit, updateTabsFit])

  React.useEffect(updateTabsFit, [updateTabsFit, selectedView])

  const viewsToDisplay = React.useMemo(() => {
    const _views = views.slice(0, howManyViewsFit)
    const selectedViewIsInList = _views.some(
      (view) => view.id === selectedView?.id
    )

    if (selectedViewIsInList || !selectedView) return _views
    _views.pop()

    return [..._views, selectedView]
  }, [howManyViewsFit, selectedView, views])

  const hasRoomForViews = viewsToDisplay.length === views.length
  const selectedViewDefaultTitle = getViewDefaultTitle(selectedView)

  return (
    <div
      ref={containerRef}
      className="flex items-center flex-grow overflow-hidden max-w-[600px]"
    >
      <Tabs
        className="flex mr-1 items-center gap-2 overflow-x-auto no-scrollbar"
        value={selectedView?.id ?? ''}
        onValueChange={(value) => {
          if (!(block.value.views ?? []).length) return
          updateSelectedView(value)
        }}
      >
        <TabsList className="!p-0 overflow-hidden !justify-start" loop={false}>
          {views.map((view) => {
            const isDisplayed = viewsToDisplay.some(({ id }) => id === view.id)
            const viewTitle =
              view.title ||
              (view.id === selectedView?.id
                ? selectedViewDefaultTitle
                : getViewDefaultTitle(view))

            return (
              <ViewContextMenu
                view={view}
                block={block}
                key={view.id}
                path={path}
                onRename={(v) => {
                  const vt =
                    v.title ||
                    (v.id === view?.id ? viewTitle : getViewDefaultTitle(view))

                  setRenamingInputValue(vt)
                  setIsRenamingViewId(v.id)
                }}
              >
                <div className="relative">
                  <TabsTrigger
                    ref={(ref) => {
                      if (ref && tabsRef.current.length < views.length) {
                        tabsRef.current.push(ref)
                      }
                    }}
                    data-view-id={view.id}
                    value={view.id}
                    asChild
                    className={clsx(
                      !isDisplayed &&
                        'invisible !absolute !duration-0 pointer-events-none select-none',
                      '[radix-state-active]:!font-normal transition-none',
                      isRenamingViewId === view.id && '!border-b-transparent'
                    )}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setRenamingInputValue(viewTitle)
                        setIsRenamingViewId(view.id)
                      }
                    }}
                  >
                    <Button
                      unstyled
                      className="group radix-state-active:text-dark-bright text-dark-muted focus-visible:outline-none focus:outline-none !rounded-none text-xs group relative cursor-default !min-w-0 !p-0"
                      onDoubleClick={() => {
                        if (readOnly) return

                        setRenamingInputValue(viewTitle)
                        setIsRenamingViewId(view.id)
                      }}
                    >
                      <div className="group-focus-visible:outline group-focus-visible:outline-1 group-focus-visible:outline-dark-control-base group-focus-visible:-outline-offset-4 rounded-lg flex items-center gap-1.5 px-3 py-1.5">
                        {isRenamingViewId === view.id && (
                          <Input
                            autoFocus
                            value={renamingInputValue}
                            onChange={(e) => {
                              setRenamingInputValue(e.target.value)
                            }}
                            onFocus={(e) => e.target.select()}
                            onBlur={async (e) => {
                              if (!replicache || !treeManager) return
                              if (e.target.value === selectedViewDefaultTitle) {
                                setIsRenamingViewId(null)
                              }

                              const params: UpdateListViewParams = {
                                id: selectedView?.id,
                                blockId: block.id,
                                path,
                                idSeed: getId(),
                                data: { title: e.target.value },
                                isoNow: new Date().toISOString(),
                                targetBlockId_withinTree: treeManager.getNode(
                                  block.typeOptions.targetBlockId,
                                  { required: true }
                                ).value.id,
                              }
                              const { undo } =
                                await replicache.mutate.updateListView(params)

                              if (undo) {
                                undoManager.current?.push({
                                  redo: [
                                    focusOpTable,
                                    { op: 'updateListView', args: params },
                                  ],
                                  undo: [
                                    focusOpTable,
                                    {
                                      op: 'blockBatchMutations',
                                      args: { mutations: undo },
                                    },
                                  ],
                                })
                              }

                              setIsRenamingViewId(null)
                              setRenamingInputValue('')
                            }}
                            className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2"
                            size="xs"
                            onKeyDown={(e) => {
                              if (e.key !== 'Escape' && e.key !== 'Enter') {
                                return
                              }
                              if (!(e.target instanceof HTMLInputElement)) {
                                return
                              }

                              e.target.blur()
                            }}
                          />
                        )}
                        <ListBlockViewIcon
                          view={view}
                          iconProps={{ width: 16, height: 16 }}
                        />
                        <span className="group-radix-state-active:font-medium whitespace-nowrap">
                          {view.title ||
                            (view.id === selectedView?.id
                              ? selectedViewDefaultTitle
                              : getViewDefaultTitle(view))}
                        </span>
                        {isRenamingViewId !== view.id && (
                          <span className="w-full absolute bottom-0 left-0 group-radix-state-active:bg-dark-control-base bg-transparent h-[1px]" />
                        )}
                      </div>
                    </Button>
                  </TabsTrigger>
                </div>
              </ViewContextMenu>
            )
          })}
        </TabsList>
      </Tabs>

      {hasRoomForViews ? (
        !readOnly && (
          <NewViewDropdownMenu
            block={block}
            path={path}
            onCreateNewView={(newView) => {
              setTimeout(() => {
                setRenamingInputValue(newView.title || 'Untitled view')
                setIsRenamingViewId(newView.id)
              }, 100)
            }}
          />
        )
      ) : (
        <ListBlockViewSwitcher
          block={block}
          path={path}
          position={{ type: 'last' }}
        >
          {() => {
            const remainingViews = views.length - viewsToDisplay.length

            return (
              <span className="max-w-[200px] text-dark-muted leading-normal hover:text-dark-dim cursor-default overflow-hidden text-ellipsis whitespace-nowrap px-1.5 pt-1 pb-1.5 text-xs outline-1 outline-dark-control-base -outline-offset-1 rounded-sm disabled:cursor-not-allowed disabled:opacity-30">
                {remainingViews} more...
              </span>
            )
          }}
        </ListBlockViewSwitcher>
      )}
    </div>
  )
}

const NewViewDropdownMenu = ({
  block,
  path,
  onCreateNewView,
}: {
  block: ListBlock
  path: string
  onCreateNewView: (view: ListView) => void
}) => {
  const replicache = useRepoReplicache().data?.replicache
  const [_, updateSelectedView] = useSelectedView(block)
  const treeManagerRef = useWorkingTreeStore((s) => s.treeRef)
  const viewCount = block.value.views?.length ?? 0
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const sourceBlockId = idToSourceBlockId(block)
  const focusOpTable = React.useMemo(() => {
    return {
      op: 'focus',
      args: {
        focus: {
          type: 'block:wrapper',
          blockId: sourceBlockId,
        },
        preferredTabKey: activeTabKey,
      },
    } satisfies RepoUndoOps[number]
  }, [sourceBlockId, activeTabKey])

  const onSelectView = React.useCallback(
    async (layout: ListView['layout']) => {
      if (!replicache || !treeManagerRef?.current) return

      const params: CreateListViewParams = {
        path,
        blockId: block.id,
        data: { title: 'New view', layout },
        position: { type: 'last' },
        id: getId(),
        isoNow: new Date().toISOString(),
        createDefaultViewIfNotExists: {
          layout: 'table',
          id: getId(),
        },
        targetBlockId_withinTree: treeManagerRef.current.getNode(
          block.typeOptions.targetBlockId,
          { required: true }
        ).value.id,
      }
      const { view: newView, undo } =
        await replicache.mutate.createListView(params)
      if (!newView) return

      if (undo) {
        undoManager.current?.push({
          redo: [focusOpTable, { op: 'createListView', args: params }],
          undo: [
            focusOpTable,
            {
              op: 'blockBatchMutations',
              args: { mutations: undo },
            },
          ],
        })
      }

      await updateSelectedView(newView.id)
      onCreateNewView(newView)
    },
    [
      block.id,
      block.typeOptions.targetBlockId,
      focusOpTable,
      onCreateNewView,
      path,
      replicache,
      treeManagerRef,
      undoManager,
      updateSelectedView,
    ]
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton className="cursor-default text-dark-muted p-1 ring-offset-0">
          <PlusIcon width={16} height={16} />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        onCloseAutoFocus={(e) => {
          if (!treeManagerRef.current) {
            e.preventDefault()
            return
          }

          const blockFromTreeManager = treeManagerRef.current?.getNode(
            block.id,
            {
              required: true,
            }
          ).value

          if (
            !isListBlock(blockFromTreeManager) ||
            viewCount !== blockFromTreeManager.value.views?.length
          ) {
            e.preventDefault()
            return
          }
        }}
      >
        <DropdownMenuItem onClick={() => onSelectView('table')}>
          <ListBlockViewIcon
            view={{ layout: 'table' }}
            iconProps={{ width: 14, height: 14, className: 'mr-2' }}
          />
          Table
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onSelectView('gallery')}>
          <ListBlockViewIcon
            view={{ layout: 'gallery' }}
            iconProps={{ width: 14, height: 14, className: 'mr-2' }}
          />
          Gallery
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onSelectView('inline')}>
          <ListBlockViewIcon
            view={{ layout: 'inline' }}
            iconProps={{ width: 14, height: 14, className: 'mr-2' }}
          />
          Inline
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const ViewContextMenu = ({
  children,
  block,
  view,
  path,
  onRename,
}: {
  children: React.ReactNode
  block: ListBlock
  view: ListView
  path: string
  onRename: (view: ListView) => void
}) => {
  const [selectedView, updateSelectedView] = useSelectedView(block)
  const { data, isLoading: isLoadingReplicache } = useRepoReplicache()
  const replicache = data?.replicache
  const treeManagerRef = useWorkingTreeStore((s) => s.treeRef)
  const editorMode = useEditorMode()
  const readOnly = editorMode === 'read'
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const sourceBlockId = idToSourceBlockId(block)
  const focusOpTable = React.useMemo(() => {
    return {
      op: 'focus',
      args: {
        focus: {
          type: 'block:wrapper',
          blockId: sourceBlockId,
        },
        preferredTabKey: activeTabKey,
      },
    } satisfies RepoUndoOps[number]
  }, [sourceBlockId, activeTabKey])

  const actions = React.useMemo(() => {
    if (isLoadingReplicache || readOnly) return []
    if (!replicache) {
      throw new Error('replicache missing')
    }

    return [
      {
        type: 'action',
        icon: <Pencil1Icon width={16} height={16} />,
        label: 'Rename',
        onSelect: () => onRename(view),
      },
      {
        type: 'action',
        icon: <CopyIcon width={16} height={16} />,
        label: 'Duplicate',
        onSelect: async () => {
          if (!replicache || !treeManagerRef.current || !replicache?.mutate) {
            return
          }

          const params: CreateListViewParams = {
            blockId: block.id,
            data: {
              title: `${view.title || getViewDefaultTitle(view)} copy`,
              layout: view.layout,
              columnsMetadata: [...view.columnsMetadata],
              filter: view.filter,
              sort: view.sort,
              showOnFirstLoad: view.showOnFirstLoad,
            },
            id: getId(),
            targetBlockId_withinTree: treeManagerRef.current.getNode(
              block.typeOptions.targetBlockId,
              { required: true }
            ).value.id,
            position: {
              type: 'after',
              targetId: view.id,
            },
            path,
            isoNow: new Date().toISOString(),
          }
          const { view: newView, undo } =
            await replicache.mutate.createListView(params)
          if (!newView) return

          if (undo) {
            undoManager.current?.push({
              redo: [focusOpTable, { op: 'createListView', args: params }],
              undo: [
                focusOpTable,
                {
                  op: 'blockBatchMutations',
                  args: { mutations: undo },
                },
              ],
            })
          }

          onRename(newView)
        },
      },
      {
        type: 'submenu',
        icon: <SymbolIcon width={16} height={16} />,
        label: 'Turn into',
        items: listViewLayouts
          .map((layout) => {
            if (layout === view.layout) return null

            return {
              type: 'action',
              icon: (
                <ListBlockViewIcon
                  view={{ layout }}
                  iconProps={{ width: 16, height: 16 }}
                />
              ),
              label: layout.charAt(0).toUpperCase() + layout.slice(1),
              intent: 'danger',
              onSelect: async () => {
                if (!treeManagerRef.current) return

                const params: UpdateListViewParams = {
                  id: view.id,
                  blockId: block.id,
                  targetBlockId_withinTree: treeManagerRef.current.getNode(
                    block.typeOptions.targetBlockId,
                    { required: true }
                  ).value.id,
                  data: { layout },
                  path,
                  isoNow: new Date().toISOString(),
                  idSeed: getId(),
                }
                const result = await replicache?.mutate.updateListView(params)
                if (result.undo) {
                  undoManager.current?.push({
                    redo: [
                      focusOpTable,
                      { op: 'updateListView', args: params },
                    ],
                    undo: [
                      focusOpTable,
                      {
                        op: 'blockBatchMutations',
                        args: { mutations: result.undo },
                      },
                    ],
                  })
                }
              },
            }
          })
          .filter(Boolean),
      },
      { type: 'separator' },
      {
        type: 'action',
        icon: <TrashIcon width={16} height={16} />,
        label: 'Delete',
        disabled: (block.value.views ?? []).length < 2,
        onSelect: async () => {
          const params: DeleteListViewParams = {
            id: view.id,
            blockId: block.id,
            path,
            isoNow: new Date().toISOString(),
          }
          const result = await replicache?.mutate.deleteListView(params)

          const newViews = result?.newViews

          if (!newViews) return

          if (result.undo) {
            undoManager.current?.push({
              redo: [focusOpTable, { op: 'deleteListView', args: params }],
              undo: [
                focusOpTable,
                {
                  op: 'blockBatchMutations',
                  args: { mutations: result.undo },
                },
              ],
            })
          }

          if (newViews?.some((v) => v.id === selectedView?.id)) return
          const oldViews = block.value.views ?? []

          const viewIndex = oldViews.findIndex((v) => v.id === view.id)
          let viewCandidate = newViews[viewIndex]

          if (!viewCandidate) {
            viewCandidate =
              newViews[viewIndex - 1] ?? newViews[viewIndex + 1] ?? newViews[0]
          }

          if (viewCandidate) {
            await updateSelectedView(viewCandidate.id)
          }
        },
      },
    ]
  }, [
    isLoadingReplicache,
    readOnly,
    replicache,
    block.value.views,
    block.id,
    block.typeOptions.targetBlockId,
    onRename,
    view,
    treeManagerRef,
    path,
    undoManager,
    focusOpTable,
    selectedView?.id,
    updateSelectedView,
  ])

  if (editorMode) return children

  const viewCount = block.value.views?.length ?? 0

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent
        className="max-w-[250px]"
        onCloseAutoFocus={(e) => {
          if (!treeManagerRef.current) {
            e.preventDefault()
            return
          }

          const blockFromTreeManager = treeManagerRef.current?.getNode(
            block.id,
            {
              required: true,
            }
          ).value

          if (
            !isListBlock(blockFromTreeManager) ||
            viewCount !== blockFromTreeManager.value.views?.length
          ) {
            e.preventDefault()
            return
          }
        }}
      >
        <ContextMenuLabel className="overflow-hidden text-ellipsis whitespace-nowrap">
          <span className="text-dark-muted">Editing view</span>{' '}
          {view.title || getViewDefaultTitle(view)}
        </ContextMenuLabel>
        {actions.map((action, index) => {
          switch (action.type) {
            case 'submenu':
              return (
                <ContextMenuSub key={index}>
                  <ContextMenuSubTrigger asChild>
                    <span className="space-x-2 text-xs leading-4">
                      {action.icon}
                      <span>{action.label}</span>
                      <ChevronRightIcon
                        width={12}
                        height={12}
                        className="text-dark-muted !ml-auto"
                      />
                    </span>
                  </ContextMenuSubTrigger>

                  <ContextMenuPortal>
                    <ContextMenuSubContent>
                      {(action.items ?? []).map((sub, index) => {
                        if (sub.type === 'separator') {
                          return <ContextMenuSeparator key={index} />
                        }

                        if (sub.type !== 'action') {
                          throw new Error('Invalid action type')
                        }

                        return (
                          <ContextMenuItem
                            key={index}
                            className="space-x-2 text-xs leading-4"
                            onSelect={sub.onSelect}
                            onClick={sub.onSelect}
                          >
                            {sub.icon}
                            <span>{sub.label}</span>
                          </ContextMenuItem>
                        )
                      })}
                    </ContextMenuSubContent>
                  </ContextMenuPortal>
                </ContextMenuSub>
              )

            case 'action':
              return (
                <ContextMenuItem
                  className="space-x-2 text-xs leading-4"
                  onClick={action.onSelect}
                  key={index}
                  disabled={action.disabled}
                >
                  {action.icon}
                  <span>{action.label}</span>
                </ContextMenuItem>
              )
            case 'separator':
              return <ContextMenuSeparator key={index} />

            default:
              console.error({ action })
              throw new Error('Unknown action type')
          }
        })}
      </ContextMenuContent>
    </ContextMenu>
  )
}
export default ListBlockActions
